import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  Grid,
  Tooltip,
} from "@mui/material";
import FieldRichText from "components/FieldRichText";
import Loader from "components/Loader";
import { API_ORGANIZATION_ID } from "config/headers";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { pick } from "lodash";
import { useCallback, useMemo, useRef, useState } from "react";
import { Field, FormProps, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  AdminListRequestOfPlaceholderFilter,
  ListResponseOfPlaceholderAdmin,
  ResponseCode,
  TemplateVersionSaveRequest,
} from "services/KnowfullyAdminClient";
import { Editor as TinyMCEEditor } from "tinymce/tinymce";
import { ReplacementText } from "types/contentTemplateTypes";
import translateError from "utils/translateError";
import PreviewDialog from "../../components/PreviewTemplateDialog";
import PreviewDataSelectorDialog from "./PreviewDataSelectorDialog";

const { Form } = withTypes<TemplateVersionSaveRequest>();

const useStyles = () => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
});

type ContentTemplateDesignFormProps = {
  onSubmit: (values: TemplateVersionSaveRequest) => Promise<any>;
  onPublishVersion: () => Promise<any>;
  onDiscardDraft: () => Promise<any>;
  onDirty?: () => void;
  onClean?: () => void;
} & FormProps<TemplateVersionSaveRequest>;

const ContentTemplateDesignForm = ({
  entity: template,
  onDirty,
  onClean,
  onSubmit,
  onPublishVersion,
  onDiscardDraft,
}: ContentTemplateDesignFormProps) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles();
  const { templateService } = useGlobalContext();
  const queryClient = useQueryClient();

  const init = useMemo(
    () => ({
      id: template?.templateVersionId,
      templateId: template?.id,
      ...pick(template ?? {}, ["content"]),
    }),
    [template]
  );

  const [isDirty, isDirtyActions] = useBoolean(false);
  const [submitting, submittingActions] = useBoolean(false);
  const [previewOpen, previewOpenActions] = useBoolean(false);
  const [previewDataSelectorOpen, previewDataSelectorActions] = useBoolean(
    false
  );
  const [organizationForPreview, setOrganizationForPreview] = useState<
    string | null | undefined
  >(template?.organizationId);

  const fechedPlaceholders = useQuery<ListResponseOfPlaceholderAdmin>(
    ["placeholders", template?.id],
    () =>
      templateService.getPlaceholders(
        AdminListRequestOfPlaceholderFilter.fromJS({
          organizationId: template.organizationId,
          pageSize: -1,
          sortField: "name",
          sortDescending: false,
          parameters: {
            templateTypeId: template?.templateTypeId!,
            templateEntityId: template?.templateEntityId!,
            excludedIds: [template?.id],
          },
        })
      ),
    {
      enabled:
        template?.id !== undefined &&
        template?.templateTypeId !== undefined &&
        template?.templateEntityId !== undefined,
    }
  );

  const editor = useRef<TinyMCEEditor>();

  const setupEditor = useCallback((e: TinyMCEEditor) => {
    editor.current = e;
  }, []);

  return useMemo(() => {
    const onDirtyInner = () => {
      onDirty && onDirty();
      isDirtyActions.setTrue();
    };

    const onCancel = () => {
      editor.current?.setContent(template?.content ?? "");
      editor.current?.setDirty(false);
      editor.current?.undoManager.clear();
      onClean && onClean();
      isDirtyActions.setFalse();
    };

    const onSubmitInner = async (values: TemplateVersionSaveRequest) => {
      try {
        submittingActions.setTrue();
        queryClient.invalidateQueries([
          "getVersionPreview",
          template!.templateVersionId!,
        ]);
        var response = await onSubmit(values);

        isDirtyActions.setValue(response?.responseCode !== ResponseCode.OK);
      } catch (err: any) {
        return { [FORM_ERROR]: translateError(err, t) };
      } finally {
        submittingActions.setFalse();
      }
    };

    const onDiscardDraftInner = async () => {
      try {
        submittingActions.setTrue();
        queryClient.invalidateQueries([
          "getVersionPreview",
          template!.templateVersionId!,
        ]);
        await onDiscardDraft();
      } catch (err: any) {
        submittingActions.setFalse();
        return { [FORM_ERROR]: translateError(err, t) };
      }
    };

    const isDefaultTemplate =
      template?.organizationId?.toLowerCase() ===
      API_ORGANIZATION_ID?.toLowerCase();

    const onPreview = async () => {
      try {
        if (!isDefaultTemplate) {
          previewOpenActions.setTrue();
        } else {
          previewDataSelectorActions.setTrue();
        }
      } catch (err: any) {
        submittingActions.setFalse();
        return { [FORM_ERROR]: translateError(err, t) };
      }
    };

    const onSetPreviewData = (organizationId: string) => {
      setOrganizationForPreview(organizationId);
      previewDataSelectorActions.setFalse();
      previewOpenActions.setTrue();
    };

    const onPublishVersionInner = async () => {
      try {
        submittingActions.setTrue();
        queryClient.invalidateQueries([
          "getVersionPreview",
          template!.templateVersionId!,
        ]);
        await onPublishVersion();
      } catch (err: any) {
        return { [FORM_ERROR]: translateError(err, t) };
      } finally {
        submittingActions.setFalse();
      }
    };

    if (fechedPlaceholders.isLoading) return <Loader />;
    if (fechedPlaceholders.isError) {
      console.log(fechedPlaceholders.error);

      return (
        <Box mt={4}>
          <Alert severity="error">
            <T id="templateLoadError" />
          </Alert>
        </Box>
      );
    }

    const placeholderList = fechedPlaceholders?.data?.data;

    const replacementText: ReplacementText[] = [
      { id: 0, textToReplace: "&gt;", textReplaceWith: ">" },
      { id: 0, textToReplace: "&amp;gt;", textReplaceWith: ">" },
    ];

    return (
      <Form
        onSubmit={onSubmitInner}
        initialValues={init}
        render={({ handleSubmit, submitError }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Field name="content">
                {({ input, meta }) => (
                  <Grid item xs={12} sx={{ mt: 3 }}>
                    <FieldRichText
                      input={input}
                      meta={meta}
                      replacementText={replacementText}
                      setupEditor={setupEditor}
                      onDirty={onDirtyInner}
                      cleanupClasses={false}
                      label={t({
                        id: template?.isDraft
                          ? "templateDesignDraftLabel"
                          : "templateDesignLabel",
                      })}
                      init={{
                        height: 470,
                        paste_data_images: true,
                        templates: placeholderList,
                      }}
                    />
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>

            <DialogActions>
              {submitting && <CircularProgress size={24} />}
              <Button
                disabled={submitting || !isDirty}
                onClick={onCancel}
                data-cy="cancelButton"
              >
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                type="submit"
                color="primary"
                disabled={submitting || !isDirty}
                data-cy="saveDraftButton"
              >
                {t({ id: "saveDraftButton" })}
              </Button>

              <Tooltip
                title={
                  template?.isDraft ? "" : t({ id: "notDraftDisabledMessage" })
                }
              >
                <div>
                  <Button
                    disabled={submitting || !template?.isDraft}
                    onClick={onDiscardDraftInner}
                    data-cy="discardDraftButton"
                  >
                    {t({ id: "discardDraftButton" })}
                  </Button>
                </div>
              </Tooltip>

              <Tooltip
                title={
                  template?.isDraft
                    ? ""
                    : t({ id: "dirtyPreviewDisabledMessage" })
                }
              >
                <div>
                  <Button
                    disabled={submitting || isDirty}
                    onClick={onPreview}
                    data-cy="previewButton"
                  >
                    {t({ id: "previewButton" })}
                  </Button>
                </div>
              </Tooltip>

              <Tooltip
                title={
                  template?.isDraft ? "" : t({ id: "notDraftDisabledMessage" })
                }
              >
                <div>
                  <Button
                    disabled={submitting || isDirty || !template?.isDraft}
                    onClick={onPublishVersionInner}
                    data-cy="publishVersionButton"
                  >
                    {t({ id: "publishVersionButton" })}
                  </Button>
                </div>
              </Tooltip>
            </DialogActions>

            <PreviewDataSelectorDialog
              onCancel={previewDataSelectorActions.setFalse}
              open={previewDataSelectorOpen}
              handleConfirm={onSetPreviewData}
            />

            <PreviewDialog
              open={previewOpen}
              handleConfirm={() => previewOpenActions.setFalse()}
              templateVersionId={template?.templateVersionId}
              organizationId={organizationForPreview!}
            />
          </form>
        )}
      />
    );
  }, [
    organizationForPreview,
    styles.feedback,
    fechedPlaceholders?.data?.data,
    fechedPlaceholders.error,
    fechedPlaceholders.isError,
    fechedPlaceholders.isLoading,
    init,
    isDirty,
    isDirtyActions,
    onClean,
    onDirty,
    onDiscardDraft,
    onPublishVersion,
    onSubmit,
    previewDataSelectorActions,
    previewDataSelectorOpen,
    previewOpen,
    previewOpenActions,
    queryClient,
    setupEditor,
    submitting,
    submittingActions,
    t,
    template,
  ]);
};

export default ContentTemplateDesignForm;
